'use client';

/**
 * Room Info Panel - displays room details with icons
 * Appended to the first section of public room detail pages
 */

import { useTranslations } from 'next-intl';
import type { Room, BedTypeEnum, ExtraBedTypeEnum } from '@/types/database';
import {
  Maximize2,
  Bed,
  Users,
  ShowerHead,
  Bath,
  Building2,
  Layers,
  BedDouble,
  Flame,
  Snowflake,
  Tv,
  Lock,
  Wine,
  TreeDeciduous,
  Wifi,
  Shirt,
  Footprints,
  Wind,
} from 'lucide-react';

interface RoomInfoPanelProps {
  room: Room;
}

// Info item component
interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-black/5 border border-black/10 hover:bg-black/10 transition-colors">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-600">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-black/50 mb-1">{label}</p>
        <p className="text-black font-medium">{value}</p>
      </div>
    </div>
  );
}

// Amenity badge component
interface AmenityBadgeProps {
  icon: React.ReactNode;
  label: string;
}

function AmenityBadge({ icon, label }: AmenityBadgeProps) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/5 border border-black/10 text-sm text-black/80 hover:bg-black/10 transition-colors">
      <span className="text-amber-600">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

export function RoomInfoPanel({ room }: RoomInfoPanelProps) {
  const t = useTranslations('roomDetail');

  // Helper functions (inside component to access `t`)

  const getSizeText = (r: Room): string | null => {
    return r.size_display || (r.size ? `${r.size} m²` : null);
  };

  const formatBedType = (bedType: BedTypeEnum): string => {
    const keyMap: Record<BedTypeEnum, string> = {
      single: 'infoPanel.bedTypes.single',
      twin: 'infoPanel.bedTypes.twin',
      double: 'infoPanel.bedTypes.double',
      king: 'infoPanel.bedTypes.king',
      'extra-king': 'infoPanel.bedTypes.extraKing',
      queen: 'infoPanel.bedTypes.queen',
    };
    return t(keyMap[bedType]) || bedType;
  };

  const getBedText = (r: Room): string | null => {
    if (r.bed_configuration_display) return r.bed_configuration_display;
    if (!r.bed_type) return null;
    const typeLabel = formatBedType(r.bed_type);
    const countPart = r.bed_count > 1 ? `${r.bed_count}× ` : '';
    const stylePart = r.bed_style ? ` (${r.bed_style})` : '';
    return `${countPart}${typeLabel}${stylePart}`;
  };

  const getOccupationText = (r: Room): string | null => {
    if (r.max_occupation_display) return r.max_occupation_display;
    const childCount = Math.max(0, (r.extra_beds || 0) - (r.extra_beds_children_only || 0));
    const parts: string[] = [t('infoPanel.adults', { count: 2 })];
    if (childCount > 0) parts.push(t('infoPanel.children', { count: childCount }));
    if (r.max_beds_babies > 0) parts.push(t('infoPanel.babies', { count: r.max_beds_babies }));
    return parts.join(', ');
  };

  const getBathroomText = (r: Room): string | null => {
    if (r.bathroom_display) return r.bathroom_display;
    const features: string[] = [];
    if (r.bathroom_has_bathtub) features.push(t('infoPanel.bathroomFeatures.bathtub'));
    if (r.bathroom_has_shower) features.push(t('infoPanel.bathroomFeatures.shower'));
    return features.length > 0 ? features.join(' & ') : null;
  };

  const formatBuildingLabel = (building: string): string => {
    const keyMap: Record<string, string> = {
      'daniel-castle': 'infoPanel.buildings.danielCastle',
      'bethlen-castle': 'infoPanel.buildings.bethlenCastle',
      'crafts-house': 'infoPanel.buildings.craftsHouse',
    };
    return keyMap[building] ? t(keyMap[building]) : building;
  };

  const formatLocationLabel = (location: string): string => {
    const keyMap: Record<string, string> = {
      'ground-floor': 'infoPanel.locations.groundFloor',
      'lower-floor': 'infoPanel.locations.lowerFloor',
      'upper-floor': 'infoPanel.locations.upperFloor',
      attic: 'infoPanel.locations.attic',
    };
    return keyMap[location] ? t(keyMap[location]) : location;
  };

  const formatExtraBedType = (type: ExtraBedTypeEnum): string => {
    const keyMap: Record<ExtraBedTypeEnum, string> = {
      'sofa-bed': 'infoPanel.extraBedTypes.sofaBed',
      rollaway: 'infoPanel.extraBedTypes.rollaway',
      'bunk-bed': 'infoPanel.extraBedTypes.bunkBed',
      cot: 'infoPanel.extraBedTypes.cot',
    };
    return t(keyMap[type]) || type;
  };

  const getExtraBedText = (r: Room): string | null => {
    if (!r.has_extra_bed) return null;
    if (r.extra_bed_description) return r.extra_bed_description;
    if (r.extra_bed_type) return formatExtraBedType(r.extra_bed_type);
    return t('infoPanel.extraBedAvailable');
  };

  // Collect all info items that have values
  const infoItems: { icon: React.ReactNode; label: string; value: string }[] = [];

  if (room.building) {
    infoItems.push({
      icon: <Building2 size={20} />,
      label: t('infoPanel.building'),
      value: formatBuildingLabel(room.building),
    });
  }

  if (room.location) {
    infoItems.push({
      icon: <Layers size={20} />,
      label: t('infoPanel.floor'),
      value: formatLocationLabel(room.location),
    });
  }

  const sizeText = getSizeText(room);
  if (sizeText) {
    infoItems.push({ icon: <Maximize2 size={20} />, label: t('infoPanel.size'), value: sizeText });
  }

  const bedText = getBedText(room);
  if (bedText) {
    infoItems.push({ icon: <Bed size={20} />, label: t('infoPanel.bed'), value: bedText });
  }

  const occupationText = getOccupationText(room);
  if (occupationText) {
    infoItems.push({ icon: <Users size={20} />, label: t('infoPanel.maxGuests'), value: occupationText });
  }

  const bathroomText = getBathroomText(room);
  if (bathroomText) {
    const hasBathtub = room.bathroom_has_bathtub;
    infoItems.push({
      icon: hasBathtub ? <Bath size={20} /> : <ShowerHead size={20} />,
      label: t('infoPanel.bathroom'),
      value: bathroomText,
    });
  }

  const extraBedText = getExtraBedText(room);
  if (extraBedText) {
    infoItems.push({ icon: <BedDouble size={20} />, label: t('infoPanel.extraBed'), value: extraBedText });
  }

  // Collect amenities
  const amenities: { icon: React.ReactNode; label: string }[] = [];

  if (room.has_fireplace) amenities.push({ icon: <Flame size={16} />, label: t('infoPanel.amenityLabels.fireplace') });
  if (room.has_air_conditioning) amenities.push({ icon: <Snowflake size={16} />, label: t('infoPanel.amenityLabels.ac') });
  if (room.has_tv) amenities.push({ icon: <Tv size={16} />, label: t('infoPanel.amenityLabels.tv') });
  if (room.has_safe) amenities.push({ icon: <Lock size={16} />, label: t('infoPanel.amenityLabels.safe') });
  if (room.has_minibar) amenities.push({ icon: <Wine size={16} />, label: t('infoPanel.amenityLabels.minibar') });
  if (room.has_balcony) amenities.push({ icon: <TreeDeciduous size={16} />, label: t('infoPanel.amenityLabels.balcony') });
  if (room.has_wifi) amenities.push({ icon: <Wifi size={16} />, label: t('infoPanel.amenityLabels.wifi') });
  if (room.has_bathrobe) amenities.push({ icon: <Shirt size={16} />, label: t('infoPanel.amenityLabels.bathrobe') });
  if (room.has_slippers) amenities.push({ icon: <Footprints size={16} />, label: t('infoPanel.amenityLabels.slippers') });
  if (room.has_hairdryer) amenities.push({ icon: <Wind size={16} />, label: t('infoPanel.amenityLabels.hairdryer') });

  // Don't render if there's nothing to show
  if (infoItems.length === 0 && amenities.length === 0) {
    return null;
  }

  return (
    <div id="room-info" className="mt-12 pt-8 border-t border-black/10">
      {/* Room Details Section */}
      {infoItems.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-black/70 uppercase tracking-wider mb-4">
            {t('infoPanel.roomDetails')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {infoItems.map((item, index) => (
              <InfoItem key={index} icon={item.icon} label={item.label} value={item.value} />
            ))}
          </div>
        </div>
      )}

      {/* Amenities Section */}
      {amenities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-black/70 uppercase tracking-wider mb-4">
            {t('infoPanel.amenities')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {amenities.map((amenity, index) => (
              <AmenityBadge key={index} icon={amenity.icon} label={amenity.label} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
